import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import Button from '../../../../components/ui/Button';
import { WpsConfirmWarning } from '../../../../models/payroll';
import { useWpsLabels } from './wpsLabels';

export const WarningList: React.FC<{ warnings: WpsConfirmWarning[] }> = ({ warnings }) => {
  const labels = useWpsLabels();
  if (warnings.length === 0) return null;
  return (
    <ul className="space-y-1.5" data-testid="wps-confirm-warnings">
      {warnings.map(w => (
        <li key={w.code} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
          <span>{labels.warning(w)}</span>
        </li>
      ))}
    </ul>
  );
};

interface InlineConfirmProps {
  warnings: WpsConfirmWarning[] | null;
  error?: string | null;
  busy?: boolean;
  /** Names the outcome, not the action that opened this. */
  confirmLabel: string;
  destructive?: boolean;
  /** False keeps the confirm button off while a required choice is missing. */
  canConfirm?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  testId?: string;
  children?: React.ReactNode;
}

/** Sits directly under the row or button that opened it, so the warning is read where the action was taken. */
export const InlineConfirm: React.FC<InlineConfirmProps> = ({
  warnings,
  error,
  busy,
  confirmLabel,
  destructive,
  canConfirm = true,
  onConfirm,
  onCancel,
  testId,
  children,
}) => {
  const { t } = useTranslation();
  return (
    <div className="mt-3 rounded-lg border border-border bg-muted/40 p-3 space-y-3" data-testid={testId}>
      {children}
      {warnings && <WarningList warnings={warnings} />}
      {error && (
        <p className="text-sm text-red-700 dark:text-red-300" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={destructive ? 'destructive' : 'default'}
          onClick={onConfirm}
          loading={busy}
          disabled={busy || !canConfirm}
          data-testid={testId ? `${testId}-confirm` : undefined}
        >
          {confirmLabel}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  );
};
